import { DatePipe, NgClass, NgStyle } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, effect, EventEmitter, input, Input, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { finalize, takeUntil } from 'rxjs';
import { DetailTableDataType } from 'src/app/models/enums/detail-data-type';
import { DocumentFlux } from 'src/app/models/enums/document-flux';
import { DocumentType } from 'src/app/models/enums/document-type';
import { PropertyType } from 'src/app/models/enums/property-type';
import { FolderDocument } from 'src/app/models/server/folder-document';
import { SpinnerService } from 'src/app/services/core/spinner.service';
import { DocumentsSectionEventsService } from 'src/app/services/events/documents-section-events.service';

import { MatSortModule, Sort } from '@angular/material/sort';
import { FaIconComponent } from '@fortawesome/angular-fontawesome';
import { faAddressBook, faEnvelope, faEnvelopeOpen, faEraser, faExpand, faPaperPlane, faReply, faTrash } from '@fortawesome/free-solid-svg-icons';
import { DocumentPropertyCodes } from '@hub4b/constantes/document-property-codes';
import { HistoryPropertyCodes } from '@hub4b/constantes/history-property-codes';
import { SortDirections } from '@hub4b/constantes/sort-directions';
import { DetailTablePanelContextMenuComponent } from '@hub4b/controls/section-table/section-table-context-menu/detail-table-panel-context-menu.component';
import { DetailTablePanelContextMenuItem } from '@hub4b/controls/section-table/section-table-context-menu/models/detail-table-panel-context-menu-item';
import { DetailTablePanelContextMenuItemEvent } from '@hub4b/controls/section-table/section-table-context-menu/models/detail-table-panel-context-menu-item-event';
import { DetailTableHeaderCellComponent } from '@hub4b/controls/section-table/section-table-header-cell/detail-table-header-cell.component';
import { BaseDirective } from '@hub4b/directives/base.directive';
import { IFilterable } from '@hub4b/models/interfaces/filterable.interface';
import { ColumnDefinition } from '@hub4b/models/server/column-definition';
import { NgxSpinnerModule } from 'ngx-spinner';
import { DraftEmailActions } from 'src/app/components/dialogs/draft-email/models/draft-email-actions';
import { TableFilterHandler } from 'src/app/core/table-filter-handler';
import { ArrayHelper } from 'src/app/helpers/array-helper';
import { ComparaisonHelper } from 'src/app/helpers/comparaison-helper';
import { DateHelper } from 'src/app/helpers/date-helper';
import { Attachment } from 'src/app/models/server/attachment';
import { DraftEmail } from 'src/app/models/server/draft-email';
import { Rights } from 'src/app/models/server/enums/rights';
import { Folder } from 'src/app/models/server/folder';
import { DialogService } from 'src/app/services/actions/dialog.service';
import { DraftEmailsService } from 'src/app/services/http/draft-emails.service';
import { ConnectedUsersStateService } from 'src/app/services/state/connected-users-state.service';
import { DocumentsStateService } from 'src/app/services/state/documents-state.service';
import { FavoriteDocumentsStateService } from 'src/app/services/state/favorite-documents-state.service';
import { FolderCommentsStateService } from 'src/app/services/state/folder-comments-state.service';
import { FolderDraftEmailsStateService } from 'src/app/services/state/folder-draft-emails-state.service';
import { DetailBaseDatasource } from '../../../datasources/detail-base-datasource';
import { DocumentsDatasource } from './data-sources/documents-datasource';
import { DocumentTableCellComponent } from './document-table-cell/document-table-cell.component';

@Component({
    selector: 'app-documents-section',
    templateUrl: './documents-section.component.html',
    styleUrls: ['./documents-section.component.scss'],
    providers: [
        {
            provide: DocumentsDatasource,
            useClass: DocumentsDatasource,
            deps: [DetailBaseDatasource],
        },
    ],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        MatSortModule,
        DetailTableHeaderCellComponent,
        DetailTablePanelContextMenuComponent,
        NgStyle,
        NgClass,
        DocumentTableCellComponent,
        NgxSpinnerModule,
        DatePipe,
        FaIconComponent,
    ]
})
export class DocumentsSectionComponent extends BaseDirective implements IFilterable, OnInit, OnDestroy {
    /**
     * Références des icônes utilisés dans le template.
     */
    public resetFilterIcon = faEraser;
    public faExpand = faExpand;
    public readIcon = faEnvelopeOpen;
    public unreadIcon = faEnvelope;
    public addressBookIcon = faAddressBook;
    public trashIcon = faTrash;
    public replyIcon = faReply;
    public faEraser = faEraser;
    public sendMailIcon = faPaperPlane;

    /**
     * Références des Enums utilisées dans le template.
     */
    public PropertyType = PropertyType;
    public DocumentType = DocumentType;
    public DocumentFlux = DocumentFlux;
    public DetailTableDataType = DetailTableDataType;

    /**
     * Références des constantes utilisées dans le template.
     */
    public FRENCH_DATETIME_FORMAT = DateHelper.FRENCH_DATETIME_FORMAT;

    /**
     * Composant du menu contextuel
     */
    @ViewChild(DetailTablePanelContextMenuComponent)
    private contextMenu: DetailTablePanelContextMenuComponent<FolderDocument>;

    /**
     * Instance du dossier pour lequel les documents sont affichés.
     */
    @Input()
    public folder: Folder;

    /**
     * Instance du document selectionné.
     */
    @Input()
    public selectedDocument: FolderDocument = null;

    /**
     * Valeur indiquant si le tableau des documents est affiché (true) ou si le tableau des brouillons de mail est affiché (false).
     */
    @Input()
    public idDocumentsTableDisplayed: boolean;

    /**
     * Valeur indiquant si le composant est affiché en mode consultation.
     */
    @Input()
    public readonly: boolean;

    /**
     * Terme rechercher dans les documents.
     */
    public documentSearchText = input(null, {
        transform: this.toLowerCaseSearch,
    });

    /**
     * Terme rechercher dans les brouillons de mail.
     */
    public draftEmailSearchText = input(null, {
        transform: this.toLowerCaseSearch,
    });

    /**
     * Emis lorsque l'utilisateur sélectionne un document.
     */
    @Output()
    public dataSelected: EventEmitter<FolderDocument> = new EventEmitter();

    /**
     * Emis lorsque l'utilisateur marque un document comme lu.
     */
    @Output()
    public dataMarkAsRead: EventEmitter<FolderDocument> = new EventEmitter();

    /**
     * Emis lorsque l'utilisateur ajoute l'emetteur d'un document aux contacts.
     */
    @Output()
    public contactAdded: EventEmitter<FolderDocument> = new EventEmitter();

    /**
     * Emis lorsque l'utilisateur Supprime un document.
     */
    @Output()
    public documentDeleted: EventEmitter<FolderDocument> = new EventEmitter();

    /**
     * Emis lorsque l'utilisateur répond à un mail.
     */
    @Output()
    public mailReplied: EventEmitter<FolderDocument> = new EventEmitter();

    /**
     * Emis lorsque l'utilisateur renvoit un mail.
     */
    @Output()
    public mailResended: EventEmitter<FolderDocument> = new EventEmitter();

    /**
     * Emis lorsque l'utilisateur supprime la recherche.
     */
    @Output()
    public searchReseted: EventEmitter<boolean> = new EventEmitter();

    /**
     * Données brutes du tableau de documents (avant filtrage)
     */
    public rawDocuments: FolderDocument[];

    /**
     * Données filtrées du tableau de documents .
     */
    public filteredDocuments: FolderDocument[];

    /**
     * Données brutes du tableau de brouillons de mail (avant filtrage)
     */
    public rawDrafts: DraftEmail[];

    /**
     * Données filtrées du tableau de brouillons de mail.
     */
    public filteredDrafts: DraftEmail[];

    /**
     * Tri courant.
     */
    public currentSort: Sort = this.datasource.defaultSort;

    /**
     * Définitions des colonnes du tableau.
     */
    public displayedColumnsDefinitions: ColumnDefinition[];

    /**
     * Gestionnaire des filtres de l'en-tête du tableau.
     */
    public tableHeaderFilterHandler: TableFilterHandler;

    /**
     * Gestionnaire des filtres de l'en-tête de la section.
     */
    public panelHeaderFilterHandler: TableFilterHandler;

    /**
     * Liste des éléments du menu contextuel.
     */
    public contextMenuItems: Array<DetailTablePanelContextMenuItem<FolderDocument>> = [];

    /**
     * Position sur l'axe X de la souris au moment d'un clic droit (utilisé pour positionner le menu contextuel)
     */
    protected rightClickMenuPositionX: number;

    /**
     * Position sur l'axe Y de la souris au moment d'un clic droit (utilisé pour positionner le menu contextuel)
     */
    protected rightClickMenuPositionY: number;

    /**
     *
     * @param documentsStateService
     * @param datasource
     * @param documentsSectionEventsService
     * @param folderCommentStateService
     * @param connectedUserStateService
     * @param favoriteDocumentsStateService
     * @param spinner
     * @param host
     */
    constructor(
        protected documentsStateService: DocumentsStateService,
        public datasource: DocumentsDatasource,
        private documentsSectionEventsService: DocumentsSectionEventsService,
        protected folderCommentStateService: FolderCommentsStateService,
        private connectedUserStateService: ConnectedUsersStateService,
        private favoriteDocumentsStateService: FavoriteDocumentsStateService,
        private draftEmailsService: DraftEmailsService,
        private folderDraftEmailsStateService: FolderDraftEmailsStateService,
        private dialogService: DialogService,
        protected spinner: SpinnerService,
        private cdr: ChangeDetectorRef
    ) {
        super();
        this.tableHeaderFilterHandler = new TableFilterHandler(this);
        this.panelHeaderFilterHandler = new TableFilterHandler(this);

        effect(() => {
            if (this.documentSearchText() !== null) {
                this.processDatas(this.rawDocuments);
            }

            if (this.draftEmailSearchText() !== null) {
                this.processDrafts(this.rawDrafts);
            }
        });
    }

    /**
     * Appelée à l'initialisation du composant.
     */
    ngOnInit(): void {
        this.displayedColumnsDefinitions = this.datasource.getColumnDefinitions();

        this.tableHeaderFilterHandler.initializeFilters(this.displayedColumnsDefinitions);
        this.panelHeaderFilterHandler.initializeFilters([
            {
                propertyCode: 'search',
                header: 'search',
                type: PropertyType.Text,
                displayFormat: null,
                size: 0,
                values: null,
                custom: false,
                isStickyColumn: false,
            },
        ]);

        this.loadDocuments();

        this.loadDraftEmails();

        this.subscribeToEvents();
    }

    /**
     * Appelée à la destruction du composant.
     */
    override ngOnDestroy(): void {
        super.ngOnDestroy();

        this.componentDestroyed$.next();
        this.componentDestroyed$.complete();

        if (this.tableHeaderFilterHandler) {
            this.tableHeaderFilterHandler.dispose();
        }

        if (this.panelHeaderFilterHandler) {
            this.panelHeaderFilterHandler.dispose();
        }
    }

    /**
     * Appelée lorsque l'utilisateur fait un clic droit sur un document pour afficher le menu contextuel.
     * @param event : Evenement.
     * @param document : Document selectionné.
     */
    public onContextMenuClick(event: MouseEvent, document: FolderDocument) {
        this.selectDocument(document);

        this.contextMenuItems = this.getContextMenuItems(document);

        // Position de la souris utilisé pour placer le menu contextuel.
        this.rightClickMenuPositionX = event.clientX;
        this.rightClickMenuPositionY = event.clientY;

        this.contextMenu.openMenu(event);
    }

    /**
     * Emit lorsque l'utilisateur selectionne un document.
     * @param dataId Instance du document.
     */
    public onDocumentSelected(data: FolderDocument): void {
        // Ne le marque pas comme lu s'il l'est déjà
        if (data.isRead === false) {
            this.markAsRead(data, true);
        }

        this.selectDocument(data);
    }

    /**
     * Emit lorsque l'utilisateur selectionne un brouillon.
     * @param dataId Instance du brouillon de mail.
     */
    public onDraftSelected(data: DraftEmail): void {
        this.selectDraft(data);
    }

    /**
     * Appelée lorsque l'utilisateur clique sur un élément du menu contextuel.
     * @param event : Événement clic du menu contextuel.
     */
    public onContextMenuItemClick(event: DetailTablePanelContextMenuItemEvent<FolderDocument>) {
        switch (event.menuItemId) {
            case 'MarkAsRead':
                this.markAsRead(event.menuItemData, true);
                break;
            case 'MarkAsUnread':
                this.markAsRead(event.menuItemData, false);
                break;
            case 'AddSenderToContact':
                this.AddSenderToContact(event.menuItemData);
                break;
            case 'DeleteDocument':
                this.deleteDocument(event.menuItemData);
                break;
            case 'ReplyToMail':
                this.replyToMail(event.menuItemData);
                break;
            case 'ResendMail':
                this.resendMail(event.menuItemData);
                break;
        }
    }

    public resetFilter() {
        this.searchReseted.emit(true);
    }

    /**
     * Souscrit aux événements des autres composants.
     */
    private subscribeToEvents() {
        this.subscribeToDocumentsSectionRefreshEvents();

        this.subscribeToFolderCommentChangeEvents();

        this.subscribeToAttachementFavoriteStateChangeEvents();

        this.subscribeToDraftEmailChangeEvents();
    }

    private subscribeToDocumentsSectionRefreshEvents() {
        this.documentsSectionEventsService.needRefresh.pipe(takeUntil(this.componentDestroyed$)).subscribe((refreshEvent) => {
            this.spinner.show('documents-spinner');
            if (!!refreshEvent && refreshEvent.folderId == this.folder.id) {
                this.processDatas(refreshEvent.documents, false);

                // Selectionne le document specifier
                let selectedDocument = refreshEvent.documents.filter((a) => (a as FolderDocument).id === refreshEvent.selectedDocumentId)[0] as FolderDocument;

                if (selectedDocument) {
                    this.selectDocument(selectedDocument);
                }

                if (refreshEvent.deleted === true) {
                    this.documentsStateService.deleteSelectedDocument(refreshEvent.folderId);
                }
            }
            this.spinner.hide('documents-spinner');
        });
    }

    private subscribeToFolderCommentChangeEvents() {
        // TODO : voir pour utiliser needRefresh en precisant de selectionner le document commentaire
        this.folderCommentStateService.onFolderCommentChange$.pipe(takeUntil(this.componentDestroyed$)).subscribe((folder) => {
            this.spinner.show('documents-spinner');
            if (!!folder && folder.id == this.folder.id) {
                this.datasource
                    .listDatas(this.folder.viewId, this.folder.id)
                    .pipe(
                        takeUntil(this.componentDestroyed$),
                        finalize(() => {
                            this.spinner.hide('documents-spinner');
                        })
                    )
                    .subscribe((details) => {
                        this.processDatas(details);
                        // Récupère la pièce jointe des commentaires à afficher
                        let commentDocument = details.filter((a) => (a as FolderDocument).type === DocumentType.Comment)[0] as FolderDocument;

                        if (commentDocument) {
                            this.selectDocument(commentDocument);
                        }
                    });
            } else {
                this.spinner.hide('documents-spinner');
            }
        });
    }

    private subscribeToDraftEmailChangeEvents() {
        this.folderDraftEmailsStateService.onFolderDraftEmailChange$.pipe(takeUntil(this.componentDestroyed$)).subscribe((event) => {
            if (!!event && event.folderId == this.folder.id) {
                this.loadDraftEmails();
            }
        });
    }

    private subscribeToAttachementFavoriteStateChangeEvents() {
        this.favoriteDocumentsStateService.onAttachmentFavoriteStateChange$.pipe(takeUntil(this.componentDestroyed$)).subscribe((attachment: Attachment) => {
            if (attachment) {
                this.spinner.show('documents-spinner');

                let updatedDocument = (this.rawDocuments as FolderDocument[]).find((document: FolderDocument) => document.id == attachment.documentId);

                if (updatedDocument) {
                    let updatedAttachment = updatedDocument.attachments.find((attachment: Attachment) => attachment.id == attachment.id);

                    if (updatedAttachment) {
                        updatedAttachment.isFavorite = attachment.isFavorite;
                    } else {
                        console.warn(
                            `Impossible de mettre à jour le status de favori de la pièce-jointe '${attachment.id}' du document '${attachment.documentId}' : La pièce-jointe ne fait pas partie de la liste des documents.`
                        );
                    }
                } else {
                    console.warn(
                        `Impossible de mettre à jour le status de favori de la pièce-jointe '${attachment.id}' du document '${attachment.documentId}' : Le document ne fait pas partie de la liste des documents.`
                    );
                }

                this.spinner.hide('documents-spinner');
            }
        });
    }

    /**
     * Retourne les éléments du menu contextuel pour le document spécifié.
     * @param document : Instance du document.
     * @returns Les éléments du menu contextuel.
     */
    private getContextMenuItems(document: FolderDocument): DetailTablePanelContextMenuItem<FolderDocument>[] {
        let menuItems: DetailTablePanelContextMenuItem<FolderDocument>[] = [];

        if (this.readonly === false && this.connectedUserStateService.hasRight(Rights.toggleReadState)) {
            if (document.isRead === false) {
                menuItems.push(new DetailTablePanelContextMenuItem('Marquer comme lu', 'MarkAsRead', document, this.readIcon));
            } else {
                menuItems.push(new DetailTablePanelContextMenuItem('Marquer comme non lu', 'MarkAsUnread', document, this.unreadIcon));
            }
        }

        if (this.readonly === false && this.connectedUserStateService.hasRight(Rights.ContactCreate)) {
            menuItems.push(new DetailTablePanelContextMenuItem("Ajouter l'emetteur au carnet d'adresses", 'AddSenderToContact', document, this.addressBookIcon));
        }

        if (this.readonly === false && this.connectedUserStateService.hasRight(Rights.DocumentDelete) && document.type !== DocumentType.Comment) {
            menuItems.push(new DetailTablePanelContextMenuItem('Supprimer le document', 'DeleteDocument', document, this.trashIcon));
        }

        if (
            this.readonly === false &&
            this.connectedUserStateService.hasRight(Rights.SendEmail) &&
            document.type === DocumentType.Email &&
            document.flux === DocumentFlux.Receive
        ) {
            menuItems.push(new DetailTablePanelContextMenuItem('Répondre au mail...', 'ReplyToMail', document, this.replyIcon));
        }

        if (
            this.readonly === false &&
            this.connectedUserStateService.hasRight(Rights.SendEmail) &&
            document.type === DocumentType.Email &&
            document.flux === DocumentFlux.Transmission
        ) {
            menuItems.push(new DetailTablePanelContextMenuItem('Renvoyer le mail', 'ResendMail', document, this.sendMailIcon));
        }

        return menuItems;
    }

    /**
     * Charge les documents du dossier.
     */
    private loadDocuments(): void {
        this.spinner.show('documents-spinner');
        this.datasource
            .listDatas(this.folder.viewId, this.folder.id)
            .pipe(
                takeUntil(this.componentDestroyed$),
                finalize(() => {
                    this.spinner.hide('documents-spinner');
                })
            )
            .subscribe((details) => {
                this.processDatas(details);
            });
    }

    /**
     * Charge les brouillons de mail du dossier.
     */
    private loadDraftEmails(): void {
        this.spinner.show('drafts-spinner');
        this.draftEmailsService
            .list(this.folder.viewId, this.folder.id)
            .pipe(
                takeUntil(this.componentDestroyed$),
                finalize(() => {
                    this.spinner.hide('drafts-spinner');
                })
            )
            .subscribe((drafts) => {
                this.rawDrafts = drafts;
                this.processDrafts(drafts);
            });
    }

    /**
     * Traite les données à afficher dans le tableau des documents.
     * @param datas Données à afficher dans le tableau.
     * @param defaultSelection Indique si la selection par défaut doit être réalisée
     */
    protected processDatas(datas: FolderDocument[], defaultSelection: boolean = true): void {
        this.rawDocuments = datas;
        this.applyFilters();

        if (this.documentSearchText() && this.documentSearchText() !== '') {
            this.filteredDocuments = this.filteredDocuments.filter(
                (x) =>
                    x.subject?.toLowerCase().includes(this.documentSearchText()) ||
                    x.label?.toLowerCase().includes(this.documentSearchText()) ||
                    x.body?.toLowerCase().includes(this.documentSearchText()) ||
                    x.comment?.toLowerCase().includes(this.documentSearchText()) ||
                    x.from?.toLowerCase().includes(this.documentSearchText()) ||
                    x.to?.toLowerCase().includes(this.documentSearchText())
            );
        }

        if (defaultSelection) {
            var commentDocument = this.filteredDocuments.find((x) => x.type === DocumentType.Comment);

            if (commentDocument) {
                this.selectDocument(commentDocument);
            } else if (this.filteredDocuments.length > 0) {
                this.selectDocument(this.filteredDocuments[0]);
            }
        }

        this.cdr.markForCheck();
    }

    /**
     * Traite les données à afficher dans le tableau des brouillons de mail.
     * @param datas Données à afficher dans le tableau.
     */
    protected processDrafts(datas: DraftEmail[]): void {
        this.rawDrafts = datas;

        this.filteredDrafts = this.rawDrafts.sort((a, b) => {
            const isAsc = false;
            return ComparaisonHelper.compare(Date.parse(a[this.currentSort.active]), Date.parse(b[this.currentSort.active]), isAsc);
        });

        if (this.draftEmailSearchText() && this.draftEmailSearchText() !== '') {
            this.filteredDrafts = this.filteredDrafts.filter(
                (x) =>
                    x.content?.toLowerCase().includes(this.draftEmailSearchText()) ||
                    x.subject?.toLowerCase().includes(this.draftEmailSearchText()) ||
                    x.to?.forEach((y) => y.toLowerCase().includes(this.draftEmailSearchText()))
            );
        }
        this.cdr.markForCheck();
    }

    /**
     * Sélectionne un document.
     * @param document : Instance du document.
     */
    private selectDocument(document: FolderDocument): void {
        if (!!document.id) {
            this.selectedDocument = document;

            this.documentsStateService.changeSelectedDocument(this.selectedDocument);
        }
    }

    /**
     * Sélectionne un brouillon de mail.
     * @param draftEmail : Instance du brouillon de mail.
     */
    private selectDraft(draftEmail: DraftEmail): void {
        this.dialogService.openDraftEmail(draftEmail).subscribe((x) => {
            if (x.action === DraftEmailActions.Deleted) {
                // Suppression du brouillon de mail
                this.draftEmailsService.delete(draftEmail.viewId, draftEmail).subscribe((x) => {
                    const index = this.rawDrafts.findIndex((x) => x.id === draftEmail.id);
                    this.rawDrafts.splice(index, 1);
                    this.processDrafts(this.rawDrafts);
                });
            } else if (x.action === DraftEmailActions.Sended) {
                // Le brouillon de mail est conservé.
                const index = this.rawDrafts.findIndex((x) => x.id === draftEmail.id);
                this.loadDraftEmails();
            } else if (x.action === DraftEmailActions.Saved) {
                // Le brouillon de mail est conservé.
                const index = this.rawDrafts.findIndex((x) => x.id === draftEmail.id);
                this.rawDrafts.splice(index, 1, x.draftEmail);
                this.processDrafts(this.rawDrafts);
            }
        });
    }

    /**
     * Marque un document comme lu ou non lu.
     * @param document Instance du document.
     * @param isRead Valeur indiquant si le document est lu (true) ou non lu (false).
     */
    private markAsRead(document: FolderDocument, isRead: boolean): void {
        if (this.readonly == false && !!document.id) {
            const index = this.rawDocuments.findIndex((x) => x.id === document.id && x.type === document.type);
            const detail = this.rawDocuments[index];
            let updatedDetail = JSON.parse(JSON.stringify(detail));
            updatedDetail.isRead = isRead;

            // Met à jour la liste des documents avec le document modifié.
            let updatedDocuments = ArrayHelper.createImmutableCopy(this.rawDocuments);
            updatedDocuments[index] = updatedDetail;
            this.processDatas(updatedDocuments, false);

            this.dataMarkAsRead.emit(updatedDetail);
        }
    }

    /**
     * Ajoute l'emetteur d'un document à la liste des contacts.
     * @param document Instance du document.
     */
    private AddSenderToContact(document: FolderDocument): void {
        if (!!document.id) {
            const detail = this.rawDocuments.find((x) => x.id === document.id && x.type === document.type);

            this.contactAdded.emit(detail);
        }
    }

    /**
     * Supprime un document.
     * @param document Instance du document.
     */
    private deleteDocument(document: FolderDocument): void {
        if (!!document.id) {
            const detail = this.rawDocuments.find((x) => x.id === document.id && x.type === document.type);

            this.documentDeleted.emit(detail);
        }
    }

    /**
     * Répond à un mail.
     * @param document Instance du document.
     */
    private replyToMail(document: FolderDocument): void {
        if (!!document.id) {
            const detail = this.rawDocuments.find((x) => x.id === document.id && x.type === document.type);

            this.mailReplied.emit(detail);
        }
    }

    /**
     * Renvoi un mail.
     * @param document Instance du document.
     */
    private resendMail(document: FolderDocument): void {
        if (!!document.id) {
            const detail = this.rawDocuments.find((x) => x.id === document.id && x.type === document.type);

            this.mailResended.emit(detail);
        }
    }

    private toLowerCaseSearch(searchText: string): string {
        if (searchText) {
            return searchText.toLowerCase();
        } else {
            return searchText;
        }
    }

    /**
     * Retourne le style du menu contextuel utilisé pour positionner le menu contextuel.
     * @returns : Le style du menu contextuel.
     */
    public getContextMenuStyle(): any {
        // Positionne le menu contextuel au niveau du pointeur de la souris.
        return {
            position: 'fixed',
            left: `${this.rightClickMenuPositionX}px`,
            top: `${this.rightClickMenuPositionY}px`,
        };
    }

    /**
     * Applique les filtres de recherche et/ou de colonnes ainsi que le tri sur la liste de dossiers.
     */
    public applyFilters(): void {
        if (!!this.rawDocuments) {
            const datas = this.rawDocuments.slice();
            this.filteredDocuments = datas.filter((x) => this.isMatchTableHeaderFilters(x) && this.isMatchPanelHeaderFilters(x));
            this.applySort();
        }
    }

    /**
     * Trie les données du tableau
     * @param sort : information sur le tri.
     */
    protected sort(sort: Sort): void {
        this.currentSort = sort;
        this.applyFilters();
    }

    /**
     * Applique le tri.
     */
    private applySort(): void {
        if (this.currentSort.direction) {
            this.filteredDocuments = this.filteredDocuments.sort((a, b) => {
                const isAsc = this.currentSort.direction === SortDirections.ASC;
                if (
                    this.currentSort.active == HistoryPropertyCodes.DATE ||
                    this.currentSort.active == DocumentPropertyCodes.CREATION_DATE ||
                    this.currentSort.active == DocumentPropertyCodes.ORIGINAL_DATE
                ) {
                    return ComparaisonHelper.compare(Date.parse(a[this.currentSort.active]), Date.parse(b[this.currentSort.active]), isAsc);
                } else {
                    return ComparaisonHelper.compare(a[this.currentSort.active], b[this.currentSort.active], isAsc);
                }
            });
        }
    }

    /**
     * Indique si une donnée respecte les filtres de l'en-tête de section.
     * @param data : donnée à tester.
     * @returns True Si la donnée respecte tous les filtres de l'en-tête de section; false autrement.
     */
    private isMatchPanelHeaderFilters(data: FolderDocument): boolean {
        if (!!this.panelHeaderFilterHandler.filterValues['search']) {
            let result = false;

            // Vérifie si au moins l'une des valeurs de cellules (autre que les cellules de type date) contient la valeur recherchée.
            result = this.displayedColumnsDefinitions.some((column) => {
                if (column.type === PropertyType.Text) {
                    const value = data[column.propertyCode];
                    const searchedValue = this.panelHeaderFilterHandler.filterValues['search'].values[0].toLowerCase();

                    if (value && value.toLowerCase().includes(searchedValue)) {
                        return true;
                    } else {
                        return false;
                    }
                } else {
                    return false;
                }
            });

            return result;
        } else {
            // Aucun paramètres de recherche --> Tous les dossiers sont retournés.
            return true;
        }
    }

    /**
     * Indique si une donnée respecte les filtres de l'en-tête du tableau.
     * @param data : donnée à tester.
     * @returns True Si la donnée respecte tous les filtres de l'en-tête du tableau; false autrement.
     */
    private isMatchTableHeaderFilters(data): boolean {
        let isMatch: boolean = true;

        this.displayedColumnsDefinitions.forEach((column) => {
            if (!!this.tableHeaderFilterHandler.filterValues[column.propertyCode]) {
                let value = data[column.propertyCode];

                if (
                    column.propertyCode == HistoryPropertyCodes.DATE ||
                    column.propertyCode == DocumentPropertyCodes.CREATION_DATE ||
                    column.propertyCode == DocumentPropertyCodes.ORIGINAL_DATE
                ) {
                    value = data.date.slice(0, 10);
                } else if (column.propertyCode == DocumentPropertyCodes.ATTACHMENTS) {
                    value = data.attachments.length > 0 ? 'true' : 'false';
                }

                let filterValue = this.tableHeaderFilterHandler.filterValues[column.propertyCode].values[0];

                if (typeof value == 'string') {
                    if (!value.toLowerCase().includes(filterValue.toLowerCase())) {
                        isMatch = false;
                    }
                } else {
                    if (!(value == filterValue)) {
                        isMatch = false;
                    }
                }
            }
        });

        return isMatch;
    }
}
